import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Wallet, Lock } from "lucide-react";
import FileUploader from "@/components/FileUploader";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const empty = {
  financial_year_id: "",
  department_id: "",
  course_branch: "",
  tuition_fee: 0,
  development_fee: 0,
  exam_fee: 0,
  other_charges: 0,
  total_annual_fee: 0,
  fees_approval_authority: "",
  approval_date: "",
  approval_doc_url: "",
  approval_doc_public_id: "",
  remarks: "",
};

const isPastFY = (fy) => {
  if (!fy?.end_date) return false;
  return new Date(fy.end_date) < new Date();
};

const fmt = (n) => new Intl.NumberFormat("en-IN").format(Math.round(n || 0));

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

export default function Fees() {
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [fys, setFys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filterFy, setFilterFy] = useState("");

  const fetchAll = async () => {
    const [info, deps, list] = await Promise.all([
      api.get("/institute/info"),
      api.get("/institute/departments"),
      api.get("/institute/fees"),
    ]);
    const sortedFys = (info.data.financial_years || []).sort(
      (a, b) => b.start_year - a.start_year,
    );
    setFys(sortedFys);
    setDepartments(deps.data);
    setItems(list.data);
    const cur = sortedFys.find((f) => f.is_current);
    if (cur) setFilterFy(cur.id);
  };

  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, []);

  const fyById = useMemo(() => Object.fromEntries(fys.map((f) => [f.id, f])), [fys]);

  const filtered = useMemo(() => {
    let arr = filterFy ? items.filter((r) => r.financial_year_id === filterFy) : items;
    return [...arr].sort((a, b) => {
      const fa = fyById[a.financial_year_id]?.start_year || 0;
      const fb = fyById[b.financial_year_id]?.start_year || 0;
      if (fa !== fb) return fb - fa;
      return (a.department_name || "").localeCompare(b.department_name || "");
    });
  }, [items, filterFy, fyById]);

  const computedTotal = (f) =>
    (parseFloat(f.tuition_fee) || 0) +
    (parseFloat(f.development_fee) || 0) +
    (parseFloat(f.exam_fee) || 0) +
    (parseFloat(f.other_charges) || 0);

  const openCreate = () => {
    const cur = fys.find((f) => f.is_current);
    setEditing(null);
    setForm({ ...empty, financial_year_id: cur?.id || filterFy || "" });
    setOpen(true);
  };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ ...empty, ...r, approval_date: r.approval_date || "" });
    setOpen(true);
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.financial_year_id) {
      toast.error("Financial year is required");
      return;
    }
    if (!form.department_id) {
      toast.error("Department is required");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      tuition_fee: parseFloat(form.tuition_fee) || 0,
      development_fee: parseFloat(form.development_fee) || 0,
      exam_fee: parseFloat(form.exam_fee) || 0,
      other_charges: parseFloat(form.other_charges) || 0,
      total_annual_fee: computedTotal(form),
      approval_date: form.approval_date || null,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/institute/fees/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Fees updated");
      } else {
        const { data } = await api.post(`/institute/fees`, payload);
        setItems((arr) => [data, ...arr]);
        toast.success("Fees added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const r = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/institute/fees/${r.id}`);
      setItems((arr) => arr.filter((x) => x.id !== r.id));
      toast.success("Fees removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  const dialogFy = fyById[form.financial_year_id];
  const dialogIsPast = isPastFY(dialogFy);

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="fees-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">FY-wise</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Fees</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Tuition, development, exam and other charges — recorded financial year by financial
            year, with the official fee approval letter attached.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="fees-add-button"
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors"
        >
          <Plus size={14} strokeWidth={1.5} /> Add Fees
        </button>
      </div>

      <div className="flex flex-wrap gap-3 items-end mb-6">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
            Financial Year
          </div>
          <select
            data-testid="fees-filter-fy"
            value={filterFy}
            onChange={(e) => setFilterFy(e.target.value)}
            className={inputCls + " w-56"}
          >
            <option value="">All financial years</option>
            {fys.map((f) => (
              <option key={f.id} value={f.id}>
                FY {f.label} {f.is_current ? "· current" : ""}
              </option>
            ))}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="fees-empty"
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
        >
          <Wallet size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">No fees records</div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="fees-table">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {[
                  "FY",
                  "Department",
                  "Tuition",
                  "Devt",
                  "Exam",
                  "Other",
                  "Total ₹",
                  "Authority",
                  "Doc",
                  "",
                ].map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const past = isPastFY(fyById[r.financial_year_id]);
                return (
                  <tr
                    key={r.id}
                    data-testid={`fees-row-${r.id}`}
                    className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                  >
                    <td className="px-4 py-4 font-mono text-xs text-[#1c1a17]">
                      {r.financial_year_label}
                      {past ? (
                        <Lock size={12} strokeWidth={1.5} className="inline ml-1 text-[#b07d2c]" />
                      ) : null}
                    </td>
                    <td className="px-4 py-4 text-[#1c1a17]">{r.department_name || "—"}</td>
                    <td className="px-4 py-4 text-[#6b655b] tabular-nums">
                      {fmt(r.tuition_fee)}
                    </td>
                    <td className="px-4 py-4 text-[#6b655b] tabular-nums">
                      {fmt(r.development_fee)}
                    </td>
                    <td className="px-4 py-4 text-[#6b655b] tabular-nums">{fmt(r.exam_fee)}</td>
                    <td className="px-4 py-4 text-[#6b655b] tabular-nums">
                      {fmt(r.other_charges)}
                    </td>
                    <td className="px-4 py-4 text-[#1c1a17] tabular-nums font-medium">
                      ₹ {fmt(r.total_annual_fee)}
                    </td>
                    <td className="px-4 py-4 text-[#6b655b]">
                      {r.fees_approval_authority || "—"}
                    </td>
                    <td className="px-4 py-4">
                      {r.approval_doc_url ? (
                        <a
                          href={r.approval_doc_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[10px] uppercase text-[#234333] border border-[#234333]/30 rounded-sm px-2 py-0.5 hover:bg-[#efece6]"
                        >
                          PDF
                        </a>
                      ) : (
                        <span className="text-xs text-[#6b655b]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(r)}
                        data-testid={`fees-edit-${r.id}`}
                        className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                      >
                        <Pencil size={14} strokeWidth={1.5} /> {past ? "View" : "Edit"}
                      </button>
                      {!past && (
                        <button
                          onClick={() => setConfirmDelete(r)}
                          data-testid={`fees-delete-${r.id}`}
                          className="ml-2 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                        >
                          <Trash2 size={14} strokeWidth={1.5} /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="fees-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Fees" : "Add Fees"}
            </DialogTitle>
          </DialogHeader>
          {dialogIsPast && (
            <div className="px-3 py-2 text-xs text-[#b07d2c] border border-[#b07d2c]/30 bg-[#b07d2c]/5 rounded-sm flex items-center gap-2">
              <Lock size={12} strokeWidth={1.5} />
              This is a past financial year. Records are read-only — only Super Admins can edit.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Financial Year *">
              <select
                data-testid="fees-fy-select"
                className={inputCls}
                value={form.financial_year_id}
                disabled={dialogIsPast && !!editing}
                onChange={(e) => set("financial_year_id", e.target.value)}
              >
                <option value="">— Select —</option>
                {fys.map((f) => (
                  <option key={f.id} value={f.id}>
                    FY {f.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Department *">
              <select
                data-testid="fees-dept-select"
                className={inputCls}
                value={form.department_id}
                onChange={(e) => set("department_id", e.target.value)}
              >
                <option value="">— Select —</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Course / Branch" full>
              <input
                data-testid="fees-course-branch"
                className={inputCls}
                value={form.course_branch}
                onChange={(e) => set("course_branch", e.target.value)}
              />
            </Field>
            <Field label="Tuition Fee (₹)">
              <input
                data-testid="fees-tuition"
                type="number"
                min="0"
                className={inputCls}
                value={form.tuition_fee}
                onChange={(e) => set("tuition_fee", e.target.value)}
              />
            </Field>
            <Field label="Development Fee (₹)">
              <input
                data-testid="fees-development"
                type="number"
                min="0"
                className={inputCls}
                value={form.development_fee}
                onChange={(e) => set("development_fee", e.target.value)}
              />
            </Field>
            <Field label="Exam Fee (₹)">
              <input
                data-testid="fees-exam"
                type="number"
                min="0"
                className={inputCls}
                value={form.exam_fee}
                onChange={(e) => set("exam_fee", e.target.value)}
              />
            </Field>
            <Field label="Other Charges (₹)">
              <input
                data-testid="fees-other"
                type="number"
                min="0"
                className={inputCls}
                value={form.other_charges}
                onChange={(e) => set("other_charges", e.target.value)}
              />
            </Field>
            <Field label="Total Annual Fee (auto)" full>
              <input
                data-testid="fees-total"
                className={inputCls + " bg-[#f9f8f6] font-medium"}
                value={`₹ ${fmt(computedTotal(form))}`}
                readOnly
              />
            </Field>
            <Field label="Fees Approval Authority">
              <input
                data-testid="fees-authority"
                className={inputCls}
                value={form.fees_approval_authority}
                onChange={(e) => set("fees_approval_authority", e.target.value)}
              />
            </Field>
            <Field label="Approval Date">
              <input
                data-testid="fees-approval-date"
                type="date"
                className={inputCls}
                value={form.approval_date}
                onChange={(e) => set("approval_date", e.target.value)}
              />
            </Field>
            <Field label="Approval Document (PDF)" full>
              <FileUploader
                kind="fees_document"
                label=""
                url={form.approval_doc_url}
                testid="fees-doc"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({
                    ...f,
                    approval_doc_url: url,
                    approval_doc_public_id: public_id,
                  }))
                }
              />
            </Field>
            <Field label="Remarks" full>
              <textarea
                data-testid="fees-remarks"
                className={inputCls + " min-h-[70px]"}
                value={form.remarks}
                onChange={(e) => set("remarks", e.target.value)}
              />
            </Field>
          </div>
          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="fees-cancel"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="fees-save"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Remove this fees record?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              FY {confirmDelete?.financial_year_label} · {confirmDelete?.department_name}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="fees-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
